import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function CiteHover({ page, excerpt, children }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span className="cursor-help rounded border-b border-dotted border-amber-400/60 bg-amber-500/10 px-1 text-amber-200" data-testid={`cite-${page}`}>{children}</span>
      {open && excerpt && (
        <span className="absolute left-0 top-full z-50 mt-2 block w-80 rounded-md border border-amber-400/40 bg-black/95 p-3 text-xs font-normal leading-relaxed text-slate-200 shadow-xl">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-amber-300">Talbot · p.{page}</span>
          {excerpt.length > 480 ? excerpt.slice(0, 480) + "…" : excerpt}
        </span>
      )}
    </span>
  );
}

// Match "Talbot, p. 34", "p. 34", "(Talbot p.34)", "p 34"
const CITE_RE = /(?:Talbot,?\s*)?(?:\(?)(?<![A-Za-z])p\.?\s*(\d{1,3})(?:\)?)/gi;

function renderWithCites(text, excerptByPage) {
  const out = [];
  let last = 0;
  let m;
  while ((m = CITE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const page = Number(m[1]);
    out.push(<CiteHover key={`${m.index}-${page}`} page={page} excerpt={excerptByPage[page]}>{m[0]}</CiteHover>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/* Only `children` (and `href` for links) are forwarded to DOM elements — no arbitrary prop spreading. */
const STYLES = {
  h1: "font-display mt-6 mb-3 text-2xl font-bold text-white",
  h2: "font-display mt-6 mb-3 text-2xl font-bold text-white",
  h3: "font-display mt-5 mb-2 text-xl font-semibold text-white",
  strong: "text-cyan-200",
  code: "font-mono-num rounded bg-white/10 px-1 py-0.5 text-cyan-200",
  ul: "my-3 list-disc space-y-1 pl-6 text-slate-300",
  ol: "my-3 list-decimal space-y-1 pl-6 text-slate-300",
  blockquote: "my-3 border-l-2 border-cyan-400/50 pl-4 text-slate-400",
  table: "my-3 w-full border border-white/10 text-sm",
  th: "border-b border-white/10 bg-white/5 px-3 py-2 text-left text-cyan-200",
  td: "border-b border-white/5 px-3 py-2",
};

const styled = (Tag, cls) => ({ children }) => <Tag className={cls}>{children}</Tag>;

const buildComponents = (excerptByPage) => {
  const cite = (children) => React.Children.map(children, (c) => (typeof c === "string" ? renderWithCites(c, excerptByPage) : c));
  return {
    h1: styled("h2", STYLES.h1),
    h2: styled("h2", STYLES.h2),
    h3: styled("h3", STYLES.h3),
    p: ({ children }) => <p className="my-3 text-slate-300">{cite(children)}</p>,
    li: ({ children }) => <li className="text-slate-300">{cite(children)}</li>,
    strong: styled("strong", STYLES.strong),
    code: styled("code", STYLES.code),
    ul: styled("ul", STYLES.ul),
    ol: styled("ol", STYLES.ol),
    blockquote: styled("blockquote", STYLES.blockquote),
    a: ({ href, children }) => <a href={href} className="text-cyan-300 hover:underline" target="_blank" rel="noreferrer">{children}</a>,
    hr: () => <hr className="my-6 border-white/10" />,
    table: styled("table", STYLES.table),
    th: styled("th", STYLES.th),
    td: styled("td", STYLES.td),
  };
};

export function LessonMarkdown({ content, excerptByPage }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={buildComponents(excerptByPage)}>{content}</ReactMarkdown>;
}
